import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  apiAuthMe,
  apiRegisterPersonal,
  apiRegisterPersonalDetails,
} from "../lib/api";
import * as API from "../lib/api"; // for optional apiCountriesIndex presence
import { useBranding } from "../lib/branding";

type Step = "account" | "details";

type Country = {
  name: string;
  iso?: string;
  dial_code?: string;
  emoji?: string;
};

function Banner({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 text-blue-700 p-3 text-sm">
      {children}
    </div>
  );
}

/** Load countries from facade (preferred), then /xapi, then /api, then window fallback */
function useCountries() {
  const [countries, setCountries] = useState<Country[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    let alive = true;

    async function load() {
      setLoading(true);
      try {
        // 1) Facade: ../lib/domains/countries.ts → re-exported via ../lib/api
        const fn: any = (API as any).apiCountriesIndex;
        if (typeof fn === "function") {
          const res = await fn();
          if (!alive) return;
          const list: Country[] =
            res?.countries ?? res?.data ?? res ?? [];
          if (Array.isArray(list) && list.length) {
            setCountries(list);
            return;
          }
        }

        // 2) Fallback endpoints
        const tryFetch = async (url: string) => {
          const r = await fetch(url, { credentials: "include" });
          if (!r.ok) return null;
          const j = await r.json();
          const arr: Country[] = j?.countries ?? j?.data ?? j ?? [];
          return Array.isArray(arr) ? arr : null;
        };

        let list =
          (await tryFetch("/xapi/countries")) ??
          (await tryFetch("/api/countries"));

        // 3) window fallback used by Blade
        if (!list || !list.length) {
          const winAny = window as any;
          if (Array.isArray(winAny?.__COUNTRIES__) && winAny.__COUNTRIES__.length) {
            list = winAny.__COUNTRIES__;
          }
        }

        if (!alive) return;
        setCountries(Array.isArray(list) ? list : []);
      } catch {
        if (!alive) return;
        setCountries([]);
      } finally {
        if (alive) setLoading(false);
      }
    }

    load();
    return () => {
      alive = false;
    };
  }, []);

  return { countries, loading };
}

export default function RegisterPersonal() {
  const branding = useBranding();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [step, setStep] = useState<Step>("account");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});

  // Step 1 (account)
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [usernameHint, setUsernameHint] = useState<string>("");

  // Step 2 (details)
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [gender, setGender] = useState<"male" | "female" | "other" | "">("");
  const [birthdate, setBirthdate] = useState("");
  const [nid, setNid] = useState("");
  const [taxId, setTaxId] = useState("");
  const [phoneCode, setPhoneCode] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [country, setCountry] = useState("");
  const [city, setCity] = useState("");
  const [addr1, setAddr1] = useState("");
  const [postal, setPostal] = useState("");

  // Countries (fixed)
  const { countries, loading: loadingCountries } = useCountries();

  // If authenticated but not explicitly sent to details, go dashboard
  useEffect(() => {
    let alive = true;
    apiAuthMe()
      .then((me) => {
        if (!alive) return;
        if (me?.user) {
          const wantDetails = params.get("step") === "details";
          if (!wantDetails) nav("/dashboard");
        }
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [nav, params]);

  // Honor ?step=details
  useEffect(() => {
    if (params.get("step") === "details") setStep("details");
  }, [params]);

  const inputCls = useMemo(
    () =>
      "mt-1 block w-full rounded-xl border px-4 py-3 text-base " +
      "border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100",
    []
  );

  function setServerErrors(e: any) {
    const errs = (e?.data?.errors as Record<string, string[]>) || {};
    setFieldErrors(errs);
  }

  function alphaOnly(u: string) {
    return /^[A-Za-z]+$/.test(u);
  }

  function validatePasswords(): string | null {
    if (!password || !password2) return "Password and confirmation are required.";
    if (password !== password2) return "Passwords do not match.";
    return null;
  }

  async function handleUsernameBlur() {
    setUsernameHint("");
    const u = username.trim();
    if (!u) return;
    if (!alphaOnly(u)) {
      setUsernameHint("Username can only contain letters a–z.");
      return;
    }
    try {
      const res = await fetch(`/check-username?username=${encodeURIComponent(u)}`, {
        credentials: "include",
        headers: { "X-Requested-With": "XMLHttpRequest" },
      });
      const data = await res.json();
      if (!data?.available) setUsernameHint("Username is already taken.");
    } catch {
      // ignore soft error
    }
  }

  async function onCreateAccount(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setFieldErrors({});
    setLoading(true);
    try {
      if (!alphaOnly(username)) {
        throw Object.assign(new Error("Invalid username"), {
          data: { errors: { username: ["Username can only contain letters a–z."] } },
        });
      }
      const pErr = validatePasswords();
      if (pErr) {
        throw Object.assign(new Error(pErr), { data: { errors: { password: [pErr] } } });
      }
      await apiRegisterPersonal({
        username: username.trim(),
        email: email.trim().toLowerCase(),
        password,
        password_confirmation: password2,
        customer_type: "personal",
      });
      setStep("details");
    } catch (e: any) {
      setError(e?.data?.message || e?.message || "Registration failed");
      setServerErrors(e);
    } finally {
      setLoading(false);
    }
  }

  function eighteenYearsAgo(): string {
    const d = new Date();
    d.setFullYear(d.getFullYear() - 18);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }

  useEffect(() => {
    if (!birthdate) setBirthdate(eighteenYearsAgo());
  }, [birthdate]);

  async function onSaveDetails(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setFieldErrors({});
    setLoading(true);
    try {
      const res = await apiRegisterPersonalDetails({
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        gender: (gender || "other") as "male" | "female" | "other",
        birthdate,
        national_id_number: nid.trim(),
        tax_id_number: taxId.trim(),
        phone_country_code: phoneCode.trim(),
        phone_number: phoneNumber.trim(),
        country: country.trim(),
        city: city.trim(),
        address_line1: addr1.trim(),
        postal_code: postal.trim(),
      });
      nav(res?.redirect?.replace(/^\/app/, "") || "/dashboard");
    } catch (e: any) {
      setError(e?.data?.message || e?.message || "Could not save details");
      setServerErrors(e);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-6">
      <div className="w-full max-w-2xl bg-white dark:bg-gray-800 rounded-2xl shadow p-6">
        <div className="text-center mb-4">
          <img src={branding.theme.logo_square_url} alt={branding.product_name} className="mx-auto mb-3 h-14 w-14 object-contain" />
          <h1 className="text-xl font-semibold text-gray-800 dark:text-gray-100">
            {step === "account" ? "Create your personal access" : "Complete your details"}
          </h1>
        </div>

        {error && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 text-red-700 p-3 text-sm">
            {error}
          </div>
        )}

        {step === "account" ? (
          <form className="space-y-5" onSubmit={onCreateAccount}>
            <div>
              <label className="block text-sm font-medium">Username (only letters)</label>
              <input
                className={inputCls}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                onBlur={handleUsernameBlur}
                autoComplete="username"
                required
              />
              {usernameHint && <p className="text-xs text-red-600 mt-1">{usernameHint}</p>}
              {fieldErrors.username?.length ? (
                <p className="text-xs text-red-600 mt-1">{fieldErrors.username[0]}</p>
              ) : null}
            </div>

            <div>
              <label className="block text-sm font-medium">Email</label>
              <input
                type="email"
                className={inputCls}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
              />
              {fieldErrors.email?.length ? (
                <p className="text-xs text-red-600 mt-1">{fieldErrors.email[0]}</p>
              ) : null}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium">Password</label>
                <input
                  type="password"
                  className={inputCls}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium">Confirm Password</label>
                <input
                  type="password"
                  className={inputCls}
                  value={password2}
                  onChange={(e) => setPassword2(e.target.value)}
                  autoComplete="new-password"
                  required
                />
              </div>
            </div>
            {fieldErrors.password?.length ? (
              <p className="text-xs text-red-600 -mt-3">{fieldErrors.password[0]}</p>
            ) : null}

            <button type="submit" disabled={loading} className="btn w-full mt-1">
              {loading ? "Creating…" : "Create account"}
            </button>

            <div className="text-center text-sm text-gray-600 dark:text-gray-300">
              Already have an account? <Link to="/login" className="text-blue-600 hover:underline">Sign in</Link>
            </div>
          </form>
        ) : (
          <form className="space-y-5" onSubmit={onSaveDetails}>
            <Banner>Your login is created. Please complete all personal details below to continue.</Banner>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium">First name</label>
                <input className={inputCls} value={firstName} onChange={(e) => setFirstName(e.target.value)} required />
                {fieldErrors.first_name?.length ? <p className="text-xs text-red-600 mt-1">{fieldErrors.first_name[0]}</p> : null}
              </div>
              <div>
                <label className="block text-sm font-medium">Last name</label>
                <input className={inputCls} value={lastName} onChange={(e) => setLastName(e.target.value)} required />
                {fieldErrors.last_name?.length ? <p className="text-xs text-red-600 mt-1">{fieldErrors.last_name[0]}</p> : null}
              </div>

              <div>
                <label className="block text-sm font-medium">Birthdate</label>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="bday"
                  placeholder="YYYY-MM-DD"
                  pattern="\\d{4}-\\d{2}-\\d{2}"
                  title="Use YYYY-MM-DD"
                  className={inputCls}
                  value={birthdate}
                  onChange={(e) => setBirthdate(e.target.value)}
                  required
                />
                <p className="mt-1 text-xs text-gray-500">Use YYYY-MM-DD.</p>
                {fieldErrors.birthdate?.length ? <p className="text-xs text-red-600 mt-1">{fieldErrors.birthdate[0]}</p> : null}
              </div>
              <div>
                <label className="block text-sm font-medium">Gender</label>
                <select className={inputCls} value={gender} onChange={(e) => setGender(e.target.value as any)} required>
                  <option value="">-- Select --</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
                {fieldErrors.gender?.length ? <p className="text-xs text-red-600 mt-1">{fieldErrors.gender[0]}</p> : null}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium">National ID Number</label>
                <input className={inputCls} value={nid} onChange={(e) => setNid(e.target.value)} required />
                {fieldErrors.national_id_number?.length ? <p className="text-xs text-red-600 mt-1">{fieldErrors.national_id_number[0]}</p> : null}
              </div>
              <div>
                <label className="block text-sm font-medium">Tax ID Number</label>
                <input className={inputCls} value={taxId} onChange={(e) => setTaxId(e.target.value)} required />
                {fieldErrors.tax_id_number?.length ? <p className="text-xs text-red-600 mt-1">{fieldErrors.tax_id_number[0]}</p> : null}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium">Phone number</label>
              <div className="flex gap-2">
                <input placeholder="Country code" className={inputCls + " w-28"} value={phoneCode} onChange={(e) => setPhoneCode(e.target.value)} required />
                <input placeholder="Phone number" className={inputCls} value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} required />
              </div>
              {fieldErrors.phone_country_code?.length ? <p className="text-xs text-red-600 mt-1">{fieldErrors.phone_country_code[0]}</p> : null}
              {fieldErrors.phone_number?.length ? <p className="text-xs text-red-600 mt-1">{fieldErrors.phone_number[0]}</p> : null}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-3">
                <label className="block text-sm font-medium">Address line 1</label>
                <input className={inputCls} value={addr1} onChange={(e) => setAddr1(e.target.value)} required />
                {fieldErrors.address_line1?.length ? <p className="text-xs text-red-600 mt-1">{fieldErrors.address_line1[0]}</p> : null}
              </div>
              <div>
                <label className="block text-sm font-medium">Postal code</label>
                <input className={inputCls} value={postal} onChange={(e) => setPostal(e.target.value)} required />
                {fieldErrors.postal_code?.length ? <p className="text-xs text-red-600 mt-1">{fieldErrors.postal_code[0]}</p> : null}
              </div>
              <div>
                <label className="block text-sm font-medium">City</label>
                <input className={inputCls} value={city} onChange={(e) => setCity(e.target.value)} required />
                {fieldErrors.city?.length ? <p className="text-xs text-red-600 mt-1">{fieldErrors.city[0]}</p> : null}
              </div>

              <div>
                  <label className="block text-sm font-medium mb-1">Country</label>
                  <select
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    required
                  >
                    <option value="" disabled>
                      {loadingCountries ? "Loading…" : "Select a country"}
                    </option>
                    {countries.map((c) => (
                      <option key={c.iso || c.name} value={c.name}>
                        {c.emoji ? `${c.emoji} ` : ""}
                        {c.name}
                      </option>
                    ))}
                  </select>
                  {fieldErrors.country?.length ? (
                    <p className="text-xs text-red-600 mt-1">{fieldErrors.country[0]}</p>
                  ) : null}
                </div>

            </div>

            <div className="flex items-center gap-2">
              <button type="button" onClick={() => setStep("account")} className="btn-white">Back</button>
              <button type="submit" disabled={loading} className="btn">
                {loading ? "Saving…" : "Save & Continue"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
